import { Box, Col, Row } from '@opendoor/atoms/box';
import { Dialog } from '@opendoor/atoms/dialog';
import { Image } from '@opendoor/atoms/image';
import { Text } from '@opendoor/atoms/text';
import { QRCodeSVG } from 'qrcode.react';

const APP_DOWNLOAD_URL = 'https://opendoor.app.link/website';
const APPLE_STORE_LINK = 'https://apps.apple.com/us/app/opendoor-buy-sell-homes/id1143708068';
const GOOGLE_PLAY_LINK =
  'https://play.google.com/store/apps/details?id=com.opendoor.buyerapp&hl=en';

const APP_STORE_BADGE_URL =
  'https://images.opendoor.com/source/s3/imgdrop-production/2023-12-1702502078078-95093.png?service=cosmos';
const GOOGLE_PLAY_BADGE_URL =
  'https://images.opendoor.com/source/s3/imgdrop-production/2023-12-1702502078081-28903.png?service=cosmos';

const QR_SIZE = 96;

const TITLE_DESKTOP = 'Get the Opendoor app to book your self-tour.';
const DESCRIPTION_DESKTOP =
  'Self-tours run from the Opendoor app. Once you’re in, unlock the door at the home with one tap.';
const TITLE_MOBILE = 'Tour any home, on your schedule';
const DESCRIPTION_MOBILE = 'Self-tours run from the Opendoor app. Install it to continue.';

export type SelfTourAppHandoffPlacement = 'footer' | 'lightbox' | 'main';

const buildAttributedUrl = (
  baseUrl: string,
  placement: SelfTourAppHandoffPlacement,
  addressToken?: string,
) => {
  const url = new URL(baseUrl);
  url.searchParams.set('utm_source', 'marketplace_pdp');
  url.searchParams.set('utm_medium', 'app_handoff_modal');
  url.searchParams.set('utm_content', placement);
  if (addressToken) {
    url.searchParams.set('utm_term', addressToken);
  }
  return url.toString();
};

export const SelfTourAppHandoffModal = ({
  isOpen,
  onClose,
  placement,
  addressToken,
}: {
  isOpen: boolean;
  onClose: () => void;
  placement: SelfTourAppHandoffPlacement;
  addressToken?: string;
}) => {
  const qrUrl = buildAttributedUrl(APP_DOWNLOAD_URL, placement, addressToken);
  const appleStoreUrl = buildAttributedUrl(APPLE_STORE_LINK, placement, addressToken);
  const googlePlayUrl = buildAttributedUrl(GOOGLE_PLAY_LINK, placement, addressToken);

  return (
    <Dialog.Root
      role="dialog"
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog>
        <Dialog.Title className="sr-only">{TITLE_DESKTOP}</Dialog.Title>
        <Dialog.Description className="sr-only">{DESCRIPTION_DESKTOP}</Dialog.Description>
        <Col className="w-full gap-4 md:w-127.5 md:flex-row md:items-stretch">
          <Col className="flex-1 justify-between gap-4">
            <Col className="gap-4">
              {/* Title — mobile copy on small screens */}
              <Text as="span" className="text-subheader-md text-od-content-primary md:hidden">
                {TITLE_MOBILE}
              </Text>
              {/* Title — desktop copy on larger screens */}
              <Text as="span" className="hidden text-subheader-md text-od-content-primary md:block">
                {TITLE_DESKTOP}
              </Text>
              {/* Description — mobile copy */}
              <Text as="p" className="text-body-md text-od-content-secondary md:hidden">
                {DESCRIPTION_MOBILE}
              </Text>
              {/* Description — desktop copy */}
              <Text as="p" className="hidden text-body-md text-od-content-secondary md:block">
                {DESCRIPTION_DESKTOP}
              </Text>
            </Col>
            <Row className="w-full gap-1.5 pt-1">
              <Box
                href={appleStoreUrl}
                external
                analyticsName={`self-tour-app-handoff-app-store-badge-${placement}`}
                aria-label="Download on the App Store"
                trackImpression
                className="flex-1 md:flex-none"
              >
                <Image
                  src={APP_STORE_BADGE_URL}
                  alt="Download on the App Store"
                  className="h-11 w-full object-contain md:w-35"
                />
              </Box>
              <Box
                href={googlePlayUrl}
                external
                analyticsName={`self-tour-app-handoff-google-play-badge-${placement}`}
                aria-label="Get it on Google Play"
                trackImpression
                className="flex-1 md:flex-none"
              >
                <Image
                  src={GOOGLE_PLAY_BADGE_URL}
                  alt="Get it on Google Play"
                  className="h-11 w-full object-contain md:w-35"
                />
              </Box>
            </Row>
          </Col>
          {/* Desktop-only QR section: gray rounded box wrapping a white rounded box around the QR. */}
          <Col
            className="hidden items-center justify-center gap-2.5 rounded-xl bg-od-bg-secondary p-5 md:flex"
            aria-label="QR code to download the Opendoor app"
            role="img"
          >
            <Box className="items-center justify-center rounded-md bg-od-bg-primary p-1.5">
              <QRCodeSVG value={qrUrl} size={QR_SIZE} />
            </Box>
            <Text as="p" className="max-w-30 text-center text-body-sm text-od-content-primary">
              Scan with your phone camera
            </Text>
          </Col>
        </Col>
      </Dialog>
    </Dialog.Root>
  );
};
